import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';

const IncidentTrendChart = ({ trendData }) => {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('daily');

  const chartOptions = [
    { value: 'line', label: 'Line Chart', icon: 'TrendingUp' },
    { value: 'bar', label: 'Bar Chart', icon: 'BarChart3' }
  ];

  const timeRangeOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const getCurrentData = () => {
    return trendData?.[timeRange] || [];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-medium">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-sm text-muted-foreground">{entry?.name}:</span>
              <span className="text-sm font-medium text-foreground">{entry?.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Incident Trends</h3>
          <p className="text-sm text-muted-foreground">Track incident patterns over time</p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {/* Time Range Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            {timeRangeOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setTimeRange(option?.value)}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                  timeRange === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {option?.label}
              </button>
            ))}
          </div>

          {/* Chart Type Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            {chartOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setChartType(option?.value)}
                className={`p-2 rounded-md transition-colors duration-200 ${
                  chartType === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title={option?.label}
              >
                <Icon name={option?.icon} size={16} />
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full h-80" aria-label="Incident Trends Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={getCurrentData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="period" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="critical" 
                stroke="var(--color-error)" 
                strokeWidth={2}
                name="Critical"
                dot={{ fill: 'var(--color-error)', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="high" 
                stroke="var(--color-warning)" 
                strokeWidth={2}
                name="High"
                dot={{ fill: 'var(--color-warning)', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="medium" 
                stroke="var(--color-accent)" 
                strokeWidth={2}
                name="Medium"
                dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="low" 
                stroke="var(--color-success)" 
                strokeWidth={2}
                name="Low"
                dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          ) : (
            <BarChart data={getCurrentData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="period" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="critical" fill="var(--color-error)" name="Critical" />
              <Bar dataKey="high" fill="var(--color-warning)" name="High" />
              <Bar dataKey="medium" fill="var(--color-accent)" name="Medium" />
              <Bar dataKey="low" fill="var(--color-success)" name="Low" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default IncidentTrendChart;